import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { BULK_INGESTION_DISPATCH_CONCURRENCY } from '../../../../shared/constants';
import { runWithConcurrencyLimit } from '../../../../shared/concurrency.util';
import { Component, OnInit, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InputFileMetadata } from '../../../../../generated.commonjs/api_pb';
import { FileMetadataService } from '../../../../shared/file-metadata.service';
import { createBillingOperationId, TaskService } from '../../../../shared/task.service';
import { ProjectCreationService } from '../../../../shared/project-creation.service';
import { TaskCounterService } from '../../../../shared/task-counter.service';
import { Router } from '@angular/router';
import { getTaskUrl } from '../../../../shared/task-navigation.util';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import { BillingService } from '../../../../shared/billing.service';
import {
  extractUserFacingErrorDetail,
  isOperationWideBillingFailure,
  showBillingAwareErrorSnackbar,
} from '../../../../shared/billing-error-utils';
import {
  OverlapResolutionDialogComponent,
  OverlapResolutionData,
  OverlapResolutionResult,
} from '../overlap-resolution-dialog/overlap-resolution-dialog.component';

export interface PageIngestionDialogData {
  file: InputFileMetadata;
  projectId: string;
  ingestedPageCount: number;
}

export interface PageIngestionDialogResult {
  action: 'ingest' | 'cancel';
  pages?: 'all' | 'custom';
  customPages?: string;
}

interface PageOverlapResult {
  overlappingPages: number[];
  newPages: number[];
  hasOverlap: boolean;
}

/** Dialog for triggering re-ingestion of selected plan pages, displaying async task progress and completion status. */
@Component({
  selector: 'app-page-ingestion-dialog',
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
  ],
  templateUrl: './page-ingestion-dialog.component.html',
  styleUrls: ['./page-ingestion-dialog.component.scss'],
})
export class PageIngestionDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<PageIngestionDialogComponent>>(MatDialogRef);
  data = inject<PageIngestionDialogData>(MAT_DIALOG_DATA);
  private fileMetadataService = inject(FileMetadataService);
  private taskService = inject(TaskService);
  private snackBar = inject(MatSnackBar);
  private projectCreationService = inject(ProjectCreationService);
  private taskCounterService = inject(TaskCounterService);
  private router = inject(Router);
  private planReviewerService = inject(PlanReviewerService);
  private dialog = inject(MatDialog);
  private billingService = inject(BillingService);

  pages: 'all' | 'custom' = 'all';
  customPages: string = '';
  infoExpanded = false;

  ngOnInit(): void {
    ClientLoggingService.log('INFO', '[PageIngestionDialog] Opened for file:', this.data.file.getFileName());
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  /**
   * Checks for overlap between requested pages and existing ingested pages FROM THE SAME FILE
   * Issue #167: Multi-file support - pages only conflict within the same file, not across files
   */
  private async checkPageOverlap(requestedPages: number[]): Promise<PageOverlapResult> {
    try {
      ClientLoggingService.log(
        'INFO',
        '[PageIngestionDialog] Checking overlap for pages:',
        requestedPages,
        'in file:',
        this.data.file.getFileName()
      );

      // Get the current file's already ingested pages from its metadata
      const fileId = this.data.file.getFileId();
      const extractedPages = this.data.file.getExtractedPagesList();
      const ingestedPageNumbers = extractedPages.map(pageStr => parseInt(pageStr, 10)).filter(num => !isNaN(num));

      ClientLoggingService.log(
        'INFO',
        '[PageIngestionDialog] Found existing ingested pages for file',
        fileId + ':',
        ingestedPageNumbers
      );

      // Calculate overlap - only within the same file
      const overlappingPages = requestedPages.filter(page => ingestedPageNumbers.includes(page));
      const newPages = requestedPages.filter(page => !ingestedPageNumbers.includes(page));

      const result: PageOverlapResult = {
        overlappingPages: overlappingPages.sort((a, b) => a - b),
        newPages: newPages.sort((a, b) => a - b),
        hasOverlap: overlappingPages.length > 0,
      };

      ClientLoggingService.log(
        'INFO',
        '[PageIngestionDialog] File-aware overlap analysis for file',
        fileId + ':',
        result
      );
      return result;
    } catch (error) {
      ClientLoggingService.log('ERROR', '[PageIngestionDialog] Error checking page overlap:', error);
      // Fallback: assume no overlap to avoid blocking user
      return {
        overlappingPages: [],
        newPages: requestedPages.sort((a, b) => a - b),
        hasOverlap: false,
      };
    }
  }

  async onStartIngestion(): Promise<void> {
    // Validate custom pages if selected
    if (this.pages === 'custom') {
      if (!this.customPages.trim()) {
        return; // Don't proceed without custom pages
      }

      // Parse and validate page numbers (supports ranges like "1-3")
      const parsedPageNumbers = this.parsePageNumbers(this.customPages);
      if (parsedPageNumbers.length === 0) {
        return; // Don't proceed with invalid page numbers
      }
      const invalidPages = parsedPageNumbers.filter(p => p <= 0 || p > this.data.file.getPageCount());
      if (invalidPages.length > 0) {
        return; // Don't proceed with invalid page numbers
      }
    }

    // Parse page numbers for task creation (supports ranges like "1-3")
    const pageNumbers = this.pages === 'custom' ? this.parsePageNumbers(this.customPages) : undefined;

    ClientLoggingService.log(
      'INFO',
      '[PageIngestionDialog] Starting ingestion for file:',
      this.data.file.getFileName()
    );

    try {
      // Calculate which pages to process
      const totalPages = this.data.file.getPageCount();
      const pagesToProcess = pageNumbers || Array.from({ length: totalPages }, (_, i) => i + 1);

      ClientLoggingService.log('INFO', '[PageIngestionDialog] Requested pages for ingestion:', pagesToProcess);

      // Check for overlap with existing pages
      const overlapResult = await this.checkPageOverlap(pagesToProcess);

      let forceReprocess = false;
      let finalPagesToProcess = pagesToProcess;

      if (overlapResult.hasOverlap) {
        ClientLoggingService.log('INFO', '[PageIngestionDialog] Overlap detected, showing resolution dialog');

        // Show overlap resolution dialog
        const dialogRef = this.dialog.open(OverlapResolutionDialogComponent, {
          width: '600px',
          maxWidth: '90vw',
          disableClose: true,
          data: {
            overlappingPages: overlapResult.overlappingPages,
            newPages: overlapResult.newPages,
            filename: this.data.file.getFileName(),
            projectId: this.data.projectId,
            fileId: this.data.file.getFileId(),
          } as OverlapResolutionData,
        });

        const result: OverlapResolutionResult | undefined = await dialogRef.afterClosed().toPromise();

        if (!result || result.action === 'cancel') {
          ClientLoggingService.log('INFO', '[PageIngestionDialog] User cancelled overlap resolution');
          return; // User cancelled
        }

        if (result.action === 'skip') {
          // Process only new pages
          finalPagesToProcess = overlapResult.newPages;
          forceReprocess = false;
          ClientLoggingService.log(
            'INFO',
            '[PageIngestionDialog] User chose to skip existing pages, processing:',
            finalPagesToProcess
          );
        } else if (result.action === 'force-reprocess') {
          // Process all pages with force reprocess
          finalPagesToProcess = pagesToProcess;
          forceReprocess = true;
          ClientLoggingService.log(
            'INFO',
            '[PageIngestionDialog] User chose to force reprocess all pages:',
            finalPagesToProcess
          );
        }

        // If no pages to process after user choice, show message and return
        if (finalPagesToProcess.length === 0) {
          this.snackBar.open('No pages to process after overlap resolution', 'Close', { duration: 3000 });
          return;
        }
      } else {
        ClientLoggingService.log(
          'INFO',
          '[PageIngestionDialog] No overlap detected, proceeding with all requested pages'
        );
      }

      ClientLoggingService.log(
        'INFO',
        '[PageIngestionDialog] Starting async ingestion for pages:',
        finalPagesToProcess
      );

      const billingOperationId = createBillingOperationId();
      if (
        await this.billingService.guardInsufficientBalance(
          this.snackBar,
          this.router,
          'plan-ingestion',
          finalPagesToProcess.length,
          billingOperationId
        )
      ) {
        return;
      }

      // Start async ingestion for each page using TaskService. Dispatch is
      // concurrency-limited (Issue #1593): firing every page's RPC via a single
      // Promise.all overwhelms the browser's own network stack on large files
      // (net::ERR_INSUFFICIENT_RESOURCES before the requests even reach ESPv2), and a
      // plain Promise.all would also discard every already-created task the moment any
      // single page failed. runWithConcurrencyLimit bounds in-flight requests and lets
      // each page settle independently.
      const {
        results: taskResponses,
        failures,
        skipped,
      } = await runWithConcurrencyLimit(
        finalPagesToProcess,
        BULK_INGESTION_DISPATCH_CONCURRENCY,
        pageNum =>
          lastValueFrom(
            this.taskService.startAsyncIngestFile(
              this.data.projectId,
              this.data.file.getFileName(),
              pageNum,
              forceReprocess, // Use user's choice or false if no overlap
              true, // enableOrientationDetection
              true, // generateExplanation (Issue #258)
              {
                operationId: billingOperationId,
                taskCount: finalPagesToProcess.length,
                childKey: String(pageNum),
              }
            )
          ),
        { stopOnFailure: isOperationWideBillingFailure }
      );

      const operationWideFailure = failures.find(failure => isOperationWideBillingFailure(failure.error));
      if (operationWideFailure) {
        ClientLoggingService.log(
          'ERROR',
          `[PageIngestionDialog] Billing integrity stopped operation ${billingOperationId}: ` +
            `${taskResponses.filter(r => r).length} started, ${skipped} not dispatched`,
          operationWideFailure.error
        );
      } else {
        failures.forEach(failure =>
          ClientLoggingService.log(
            'ERROR',
            `[PageIngestionDialog] Failed to start ingestion for page ${failure.item}:`,
            failure.error
          )
        );
      }
      ClientLoggingService.log(
        'INFO',
        `[PageIngestionDialog] Started ${taskResponses.filter(r => r).length} of ${finalPagesToProcess.length} tasks` +
          (failures.length > 0 ? ` (${failures.length} failed, ${skipped} not dispatched)` : '')
      );

      // Add tasks to the task counter service for tracking using real task IDs
      taskResponses.forEach((response, index) => {
        if (response) {
          const taskId = response.getTaskId();
          const pageNum = finalPagesToProcess[index];

          ClientLoggingService.log('INFO', `[PageIngestionDialog] Adding task ${taskId} for page ${pageNum}`);

          this.taskCounterService.addTask(
            taskId,
            this.data.projectId,
            `${this.data.file.getFileName()} (Page ${pageNum})`,
            {
              file_id: this.data.file.getFileId(),
              page_number: pageNum,
              filename: this.data.file.getFileName(),
            }
          );
        }
      });

      const startedResponses = taskResponses.filter((r): r is NonNullable<typeof r> => !!r);
      const taskCount = startedResponses.length;

      if (taskCount === 0 && finalPagesToProcess.length > 0) {
        // Every page failed to dispatch — mirror ProjectCreationService's `success: false` path
        // instead of closing the dialog with a misleading "0 of N queued, started: true" result.
        // Leave the dialog open (like the catch block below) so the user can see the error and
        // retry immediately rather than having to reopen it from scratch.
        const firstError = failures[0]?.error;
        const errorMsg = firstError instanceof Error ? firstError.message : String(firstError);
        const userMessage =
          extractUserFacingErrorDetail([errorMsg]) ||
          `Failed to start ingestion for all ${finalPagesToProcess.length} requested page(s): ${errorMsg}`;
        showBillingAwareErrorSnackbar(this.snackBar, this.router, userMessage);
        return;
      }

      const singleTaskId = taskCount === 1 ? startedResponses[0].getTaskId() : undefined;
      const actionLabel = singleTaskId ? 'View Task' : 'View Tasks';
      const snackBarMessage =
        failures.length > 0
          ? `${taskCount} of ${finalPagesToProcess.length} page${finalPagesToProcess.length === 1 ? '' : 's'} queued; ${failures.length} failed to start`
          : `${taskCount} page${taskCount === 1 ? '' : 's'} queued for processing`;
      const snackBarRef = this.snackBar.open(snackBarMessage, actionLabel, {
        duration: failures.length > 0 ? 8000 : 5000,
      });

      snackBarRef.onAction().subscribe(() => {
        ClientLoggingService.log(
          'INFO',
          `[PageIngestionDialog] "${actionLabel}" clicked from snackbar - navigating to tasks page`
        );
        this.router.navigate(getTaskUrl(this.data.projectId, singleTaskId));
      });

      // Close dialog immediately with success result
      this.dialogRef.close({
        action: 'ingest',
        pages: this.pages,
        customPages: this.customPages,
        taskCount: taskCount,
        started: true,
        taskIds: startedResponses.map(r => r.getTaskId()),
      });
    } catch (error) {
      ClientLoggingService.log('ERROR', '[PageIngestionDialog] Error starting ingestion:', error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      const userMessage = extractUserFacingErrorDetail([errorMsg]) || `Failed to start page ingestion: ${errorMsg}`;
      showBillingAwareErrorSnackbar(this.snackBar, this.router, userMessage);
    }
  }

  // Helper methods for template
  getIngestedPagesText(): string {
    const ingested = this.data.ingestedPageCount;
    const total = this.data.file.getPageCount();

    if (ingested === 0) {
      return 'No pages ingested yet';
    } else if (ingested === total) {
      return `All ${total} pages already ingested`;
    } else {
      return `${ingested} of ${total} pages ingested`;
    }
  }

  isCustomPagesValid(): boolean {
    if (this.pages !== 'custom') return true;
    if (!this.customPages.trim()) return false;

    // Parse page numbers (supports ranges like "1-3")
    const pageNumbers = this.parsePageNumbers(this.customPages);
    if (pageNumbers.length === 0) return false;

    return pageNumbers.every(p => p > 0 && p <= this.data.file.getPageCount());
  }

  canStartIngestion(): boolean {
    return this.isCustomPagesValid();
  }

  getDocumentTypeDisplay(): string {
    return this.fileMetadataService.formatDocumentType(this.data.file.getDocumentType());
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getRemainingPagesText(): string {
    const total = this.data.file.getPageCount();
    const ingested = this.data.ingestedPageCount;
    const remaining = total - ingested;

    if (remaining <= 0) {
      return 'all pages already ingested';
    } else {
      return `${remaining} remaining of ${total}`;
    }
  }

  toggleInfoExpanded(): void {
    this.infoExpanded = !this.infoExpanded;
  }

  /**
   * Parse page numbers string that supports ranges (e.g., "1,2,3-5,7")
   * This method mirrors the implementation in ProjectCreationService
   */
  private parsePageNumbers(pagesStr: string): number[] {
    const pages: number[] = [];
    const parts = pagesStr.split(',');

    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed.includes('-')) {
        // Handle range like "3-5"
        const [start, end] = trimmed.split('-').map(s => parseInt(s.trim(), 10));
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            if (!pages.includes(i)) {
              pages.push(i);
            }
          }
        }
      } else {
        // Handle individual page number
        const pageNum = parseInt(trimmed, 10);
        if (!isNaN(pageNum) && !pages.includes(pageNum)) {
          pages.push(pageNum);
        }
      }
    }

    return pages.sort((a, b) => a - b); // Sort pages in ascending order
  }
}
